// src/service/websocket.js

import { store } from "../Redux/Store";
import { setDevices, setWebSocketData } from "../Redux/service/Websocket";
import { throttle } from "lodash";

class WebSocketManager {
	constructor() {
		this.socket = null;
		this.tasks = null;
		this.shouldReconnect = false;
		this.reconnectTimer = null;
		this.reconnectDelay = 1000;
		this.accumulatedData = {
			devices: [],
			positions: [],
			events: [],
		};

		// Throttled dispatch to reduce frequency of Redux updates
		this.throttledDispatch = throttle(() => {
			if (
				this.accumulatedData.devices.length === 0 &&
				this.accumulatedData.positions.length === 0 &&
				this.accumulatedData.events.length === 0
			) {
				return; // Nothing to dispatch
			}

			// Deduplicate by deviceId
			const uniqueDevices = new Map();
			for (const device of this.accumulatedData.devices) {
				uniqueDevices.set(device.id, device);
			}

			const uniquePositions = new Map();
			for (const pos of this.accumulatedData.positions) {
				uniquePositions.set(pos.deviceId, pos);
			}

			const uniqueEvents = new Map();
			for (const event of this.accumulatedData.events) {
				uniqueEvents.set(event.deviceId, event);
			}
			// Dispatch the merged & deduplicated data
			store.dispatch(
				setWebSocketData({
					devices: Array.from(uniqueDevices.values()),
					positions: Array.from(uniquePositions.values()),
					events: Array.from(uniqueEvents.values()),
				}),
			);

			// Clear buffer after dispatch
			this.accumulatedData = {
				devices: [],
				positions: [],
				events: [],
			};
		}, 800); // 500ms throttle
	}

	// Traccar's socket only sends positions on connect; device status is pushed
	// only when a device next reports. Seed the current statuses from REST.
	async loadDevices() {
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/devices`, {
				credentials: "include",
				headers: { Accept: "application/json" },
			});
			if (!response.ok) return;
			const devices = await response.json();
			if (devices?.length) {
				// Merge into the stored list so it survives regardless of which page is mounted;
				// keep any fresher socket updates already received.
				const merged = new Map(devices.map((d) => [d.id, d]));
				for (const d of store.getState().webSocket.devices) {
					merged.set(d.id, { ...merged.get(d.id), ...d });
				}
				store.dispatch(setDevices([...merged.values()]));
			}
		} catch (error) {
			console.error("Failed to load devices:", error);
		}
	}

	connect() {
		this.shouldReconnect = true;
		clearTimeout(this.reconnectTimer);
		if (
			this.socket &&
			(this.socket.readyState === WebSocket.OPEN ||
				this.socket.readyState === WebSocket.CONNECTING)
		) {
			return;
		}

		this.socket = new WebSocket(process.env.REACT_APP_WSSOCKET_URL);

		this.socket.onopen = () => {
			console.log("WebSocket connected");
			this.reconnectDelay = 1000;
			this.loadDevices();
		};

		this.socket.onmessage = (event) => {
			const parsedData = JSON.parse(event.data);

			if (parsedData.devices?.length) {
				this.accumulatedData.devices.push(...parsedData.devices);
			}
			if (parsedData.positions?.length) {
				this.accumulatedData.positions.push(...parsedData.positions);
			}
			if (parsedData.events?.length) {
				this.accumulatedData.events.push(...parsedData.events);
			}

			// Call throttled dispatch
			this.throttledDispatch();
		};

		this.socket.onclose = () => {
			console.log("WebSocket disconnected");
			if (!this.shouldReconnect) return;
			this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectDelay);
			this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
		};

		this.socket.onerror = (error) => {
			console.error("WebSocket error:", error);
		};
	}

	disconnect() {
		this.shouldReconnect = false;
		clearTimeout(this.reconnectTimer);
		if (this.socket) {
			this.socket.close();
		}
	}
}

export const webSocketManager = new WebSocketManager();
